import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { FileText, Download, Mail, Printer, Sparkles, Copy, RotateCcw, Settings, Loader2, Video, Link } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { quoteIntroApi, quotesApi } from '../services/api';
import VideoRecorder from './VideoRecorder';
import { generateQuoteHTML } from '../utils/htmlToPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PdfPreview = ({ companyInfo, customerInfo, selectedServices, selectedElements = {}, subpageCount, pricing, discount, quoteIntro, additionalInfo, nextSteps, customerArguments, onIntroGenerated, savedQuoteId, paymentTerms, customPaymentTerms, savedCustomTerms, quoteSections, deliveryTime, validity, warranty, includeDetailedDescriptions: includeDetailedDescriptionsProp, onIncludeDetailedDescriptionsChange }) => {
  
  // Get company colors with defaults
  const companyColors = {
    einmalig: companyInfo?.colorEinmalig || '#22c55e',
    monatlich: companyInfo?.colorMonatlich || '#f97316',
    jaehrlich: companyInfo?.colorJaehrlich || '#a855f7',
    primary: companyInfo?.primaryColor || '#3b82f6',
    secondary: companyInfo?.secondaryColor || '#8b5cf6',
    accent: companyInfo?.accentColor || '#f59e0b'
  }
  
  // State for saved quote number
  const [savedQuoteNumber, setSavedQuoteNumber] = useState(null);
  
  // Debug logging
  console.log('🔍 PdfPreview selectedServices:', selectedServices)
  console.log('🔍 PdfPreview savedQuoteId:', savedQuoteId)
  console.log('🔍 PdfPreview quoteSections:', quoteSections)
  
  // Fetch quote number when savedQuoteId changes
  useEffect(() => {
    const fetchQuoteNumber = async () => {
      if (savedQuoteId) {
        try {
          const response = await quotesApi.get(savedQuoteId);
          const quote = response.data || response;
          if (quote?.quoteNumber) {
            console.log('🔍 Fetched quoteNumber:', quote.quoteNumber);
            setSavedQuoteNumber(quote.quoteNumber);
          }
        } catch (error) {
          console.error('Error fetching quote number:', error);
        }
      } else {
        setSavedQuoteNumber(null);
      }
    };
    
    fetchQuoteNumber();
  }, [savedQuoteId]);
  console.log('🔍 PdfPreview quoteSections length:', quoteSections?.length)
  
  // Video state
  const [quoteVideos, setQuoteVideos] = useState([])
  const [showVideoRecorder, setShowVideoRecorder] = useState(false)
  
  // KI Intro state - store locally if not provided via props
  const [localIntroText, setLocalIntroText] = useState(quoteIntro || '')
  
  const generateQuoteNumber = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `WD-${year}${month}${day}-${random}`;
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const calculateSubpagePrice = () => {
    if (subpageCount <= 5) {
      return subpageCount * 350;
    } else {
      return subpageCount * 250;
    }
  };

  // Use prop if provided, otherwise local state
  const [includeDetailedDescriptions, setIncludeDetailedDescriptions] = React.useState(includeDetailedDescriptionsProp || false);
  
  // Sync with prop changes
  React.useEffect(() => {
    if (includeDetailedDescriptionsProp !== undefined) {
      setIncludeDetailedDescriptions(includeDetailedDescriptionsProp);
    }
  }, [includeDetailedDescriptionsProp]);
  
  // Notify parent of changes
  const handleIncludeDetailedDescriptionsChange = (value) => {
    setIncludeDetailedDescriptions(value);
    if (onIncludeDetailedDescriptionsChange) {
      onIncludeDetailedDescriptionsChange(value);
    }
  };
  const [isIntroDialogOpen, setIsIntroDialogOpen] = useState(false);
  const [isGeneratingIntro, setIsGeneratingIntro] = useState(false);
  const [introFormData, setIntroFormData] = useState({
    company_name: customerInfo?.company || '',
    industry: '',
    contact_person: customerInfo?.name || '',
    project_goal: '',
    website_url: '',
    current_status: '',
    challenges: '',
    budget: '',
    timeline: '',
    special_requirements: '',
    tone: 'professional' // Neu: Stil-Auswahl
  });
  const [tempGeneratedIntro, setTempGeneratedIntro] = useState('');
  const [tempAdditionalInfo, setTempAdditionalInfo] = useState('');
  
  const { toast } = useToast();

  // Update introFormData when customerInfo changes
  React.useEffect(() => {
    setIntroFormData(prev => ({
      ...prev,
      company_name: customerInfo?.company || '',
      contact_person: customerInfo?.name || ''
    }));
  }, [customerInfo]);

  // Predefined options for dropdowns
  const industries = [
    'Webdesign', 'Friseur/Beauty', 'Rechtsberatung', 'Gastronomie', 'Immobilien', 
    'IT-Beratung', 'Marketing', 'Gesundheitswesen', 'Einzelhandel', 'Handwerk',
    'Beratung', 'Finanzdienstleistungen', 'Bildung', 'Automotive', 'Wellness'
  ];

  // Branchenspezifische Auswahlen
  const getProjectGoalsByIndustry = (industry) => {
    const goals = {
      'Webdesign': [
        'Neue Website erstellen',
        'Bestehende Website modernisieren', 
        'Online-Shop einrichten',
        'Suchmaschinenoptimierung',
        'Mobile Optimierung',
        'Eigene Eingabe'
      ],
      'Friseur/Beauty': [
        'Online-Buchungssystem einrichten',
        'Moderne Website für Salon',
        'Local SEO für mehr Kunden',
        'Before/After Galerie erstellen',
        'Social Media Integration',
        'Eigene Eingabe'
      ],
      'Gastronomie': [
        'Online-Reservierungssystem',
        'Speisekarte digital präsentieren',
        'Lieferservice-Website',
        'Event-Buchungen ermöglichen',
        'Local SEO für Restaurant',
        'Eigene Eingabe'
      ],
      'Immobilien': [
        'Immobilien-Portal erstellen',
        'Virtual Tours integrieren',
        'Lead-Generierung optimieren',
        'Property Management System',
        'SEO für Immobilienmakler',
        'Eigene Eingabe'
      ]
    }
    return goals[industry] || [
      'Digitale Präsenz aufbauen',
      'Online-Sichtbarkeit erhöhen', 
      'Kundengewinnung digitalisieren',
      'Prozesse digitalisieren',
      'Marktposition stärken',
      'Eigene Eingabe'
    ]
  }

  const getCurrentStatusByIndustry = (industry) => {
    const statuses = {
      'Webdesign': ['Keine Website vorhanden', 'Veraltete Website', 'Website modernisieren', 'Neue Funktionen hinzufügen', 'Eigene Eingabe'],
      'Friseur/Beauty': ['Keine Online-Buchung', 'Veraltete Salon-Website', 'Kein Local SEO', 'Fehlende Mobile-Optimierung', 'Eigene Eingabe'],
      'Gastronomie': ['Keine Online-Reservierung', 'Veraltete Restaurant-Website', 'Keine Speisekarte online', 'Fehlende Lieferservice-Integration', 'Eigene Eingabe'],
      'Immobilien': ['Keine Immobilien-Website', 'Veraltetes Exposé-System', 'Keine Virtual Tours', 'Fehlende Lead-Generierung', 'Eigene Eingabe']
    }
    return statuses[industry] || ['Keine Website vorhanden', 'Veraltete Website', 'Modernisierung benötigt', 'Neue Funktionen hinzufügen', 'Eigene Eingabe']
  }

  const handleGenerateIntro = async () => {
    if (!introFormData.company_name || !introFormData.industry || !introFormData.current_status) {
      toast({
        title: "Eingabe unvollständig",
        description: "Bitte füllen Sie mindestens Firmenname, Branche und aktuellen Status aus.",
        variant: "destructive",
      });
      return;
    }

    setIsGeneratingIntro(true);
    try {
      // Get selected services info for context
      const selectedServicesList = Object.values(selectedServices || {})
      const serviceNames = selectedServicesList.map(s => s.name || s.id).join(', ')
      const serviceCount = selectedServicesList.length
      
      // Enhanced request with service context
      const introRequest = {
        ...introFormData,
        // Add service context
        selected_services: serviceNames,
        service_count: serviceCount,
        project_value: pricing?.total || 0,
        // Use custom entries if "Eigene Eingabe" was selected
        project_goal: introFormData.project_goal === 'Eigene Eingabe' ? 
          introFormData.custom_project_goal || introFormData.project_goal : 
          introFormData.project_goal,
        current_status: introFormData.current_status === 'Eigene Eingabe' ? 
          introFormData.custom_current_status || introFormData.current_status : 
          introFormData.current_status
      };

      console.log('🔍 Generating intro with services context:', introRequest)

      const response = await quoteIntroApi.generate(introRequest);
      console.log('🔍 Intro API response:', response)
      
      // Handle different response formats from backend
      const introText = response.intro_text || response.intro || response.data?.intro_text || '';
      const additionalText = response.additional_info || response.additionalInfo || response.data?.additional_info || '';
      
      if (introText && introText.length > 50) {
        setTempGeneratedIntro(introText);
        setTempAdditionalInfo(additionalText);
        
        toast({
          title: "Intro erfolgreich generiert ✅",
          description: `Der KI-generierte Intro wurde erstellt (${introText.length} Zeichen).`,
        });
      } else {
        console.log('Response data:', response);
        toast({
          title: "Leere Antwort erhalten",
          description: "Die KI hat keine verwertbare Antwort gesendet. Bitte versuchen Sie es mit anderen Daten.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error generating intro:', error);
      toast({
        title: "Fehler bei der Generierung",
        description: `Fehler: ${error.message || 'Unbekannter Fehler'}`,
        variant: "destructive",
      });
    } finally {
      setIsGeneratingIntro(false);
    }
  };

  const handleUseIntro = () => {
    setLocalIntroText(tempGeneratedIntro)
    if (onIntroGenerated) {
      onIntroGenerated(tempGeneratedIntro, tempAdditionalInfo);
    }
    setIsIntroDialogOpen(false);
    toast({
      title: "Intro übernommen",
      description: "Der generierte Intro wurde in das Angebot eingefügt.",
    });
  };

  const handleResetForm = () => {
    setIntroFormData({
      company_name: customerInfo?.company || '',
      industry: '',
      contact_person: customerInfo?.name || '',
      project_goal: '',
      website_url: '',
      current_status: '',
      challenges: '',
      budget: '',
      timeline: '',
      special_requirements: '',
      tone: 'professional' // Neu: Stil-Auswahl
    });
    setTempGeneratedIntro('');
    setTempAdditionalInfo('');
  };

  // MODULARES SYSTEM: Nur die tatsächlich ausgewählten Services anzeigen
  const quoteItems = [
    ...Object.entries(selectedServices || {}).flatMap(([key, service], index) => {
      const items = []
      
      // WICHTIG: In PDF/Vorschau wird IMMER die Hauptposition im Preis berechnet
      // Alternativen werden nur als eingerückte Optionen angezeigt
      let effectivePrice = (service.price || service.originalPrice || 0)
      
      // LEGACY: Altes Alternative-System (falls noch vorhanden)
      if (service.alternatives && service.alternatives.length > 0 && service.selectedAlternativeId) {
        const selectedAlt = service.alternatives.find(a => a.id === service.selectedAlternativeId)
        if (selectedAlt) {
          effectivePrice = selectedAlt.price
        }
      }
      
      const basePrice = effectivePrice
      const quantity = service.quantity || 1
      const subtotal = basePrice * quantity
      
      // Calculate individual service discount
      let serviceDiscount = 0
      if (service.customDiscount?.value > 0) {
        if (service.customDiscount.type === 'percentage') {
          serviceDiscount = subtotal * (service.customDiscount.value / 100)
        } else {
          serviceDiscount = service.customDiscount.value
        }
      }
      
      const finalTotal = Math.max(0, subtotal - serviceDiscount)
      
      // Hauptposition (wird immer berechnet)
      const mainItem = {
        position: index + 1,
        description: service.name || '',
        details: service.description || '',
        detailedDescription: service.detailedDescription || '',
        quantity: quantity,
        unitPrice: basePrice,
        subtotal: subtotal,
        serviceDiscount: serviceDiscount,
        customDiscountInfo: service.customDiscount,
        total: finalTotal,
        type: service.type || 'einmalig',
        notice: service.notice || '',
        isOptional: service.isOptional || false,
        notes: service.notes || '',
        hasAlternative: service.hasAlternative || false,
        isAlternativeActive: false // In PDF ist Alternative nie aktiv
      }
      
      items.push(mainItem)
      
      // Wenn Alternative existiert, zeige sie als eingerückte Option (NICHT im Preis)
      if (service.hasAlternative && service.alternativeName) {
        const altItem = {
          position: `${index + 1}a`,
          description: service.alternativeName,
          details: service.alternativeDescription || 'Alternative Position',
          detailedDescription: '',
          quantity: quantity,
          unitPrice: service.alternativePrice || 0,
          subtotal: (service.alternativePrice || 0) * quantity,
          serviceDiscount: 0,
          customDiscountInfo: null,
          total: (service.alternativePrice || 0) * quantity,
          type: service.type || 'einmalig',
          notice: '',
          isOptional: true, // Alternative ist immer optional wenn nicht gewählt
          isAlternativeOption: true,
          parentService: service.name,
          notes: '⚡ Alternative zur Position ' + (index + 1)
        }
        items.push(altItem)
      }
      
      return items
    })
  ];

  // Separate one-time, monthly, and yearly items
  const oneTimeItems = quoteItems.filter(item => item.type !== 'monatlich' && item.type !== 'jährlich');
  const monthlyItems = quoteItems.filter(item => item.type === 'monatlich');
  const yearlyItems = quoteItems.filter(item => item.type === 'jährlich');

  // Calculate pricing with service discounts and individual notes
  const calculatePricing = () => {
    const einmaligServices = quoteItems.filter(item => item.type === 'einmalig' && !item.isOptional)
    const monatlichServices = quoteItems.filter(item => item.type === 'monatlich' && !item.isOptional)
    const jaehrlichServices = quoteItems.filter(item => item.type === 'jährlich' && !item.isOptional)
    
    // Use the already calculated totals from quoteItems (which include service discounts)
    const einmaligSubtotal = einmaligServices.reduce((sum, item) => sum + (item.total || 0), 0)
    const monatlichSubtotal = monatlichServices.reduce((sum, item) => sum + (item.total || 0), 0)
    const jaehrlichSubtotal = jaehrlichServices.reduce((sum, item) => sum + (item.total || 0), 0)
    const laufendSubtotal = monatlichSubtotal + jaehrlichSubtotal
    
    // Apply global discounts
    let einmaligDiscount = 0
    let monatlichDiscount = 0
    let jaehrlichDiscount = 0
    
    if (discount?.oneTime?.value > 0) {
      if (discount.oneTime.type === 'percentage') {
        einmaligDiscount = einmaligSubtotal * (discount.oneTime.value / 100)
      } else {
        einmaligDiscount = Math.min(discount.oneTime.value, einmaligSubtotal)
      }
    }
    
    if (discount?.monthly?.value > 0) {
      if (discount.monthly.type === 'percentage') {
        monatlichDiscount = monatlichSubtotal * (discount.monthly.value / 100)
      } else {
        monatlichDiscount = Math.min(discount.monthly.value, monatlichSubtotal)
      }
    }
    
    if (discount?.yearly?.value > 0) {
      if (discount.yearly.type === 'percentage') {
        jaehrlichDiscount = jaehrlichSubtotal * (discount.yearly.value / 100)
      } else {
        jaehrlichDiscount = Math.min(discount.yearly.value, jaehrlichSubtotal)
      }
    }
    
    return {
      einmaligSubtotal,
      monatlichSubtotal,
      jaehrlichSubtotal,
      laufendSubtotal,
      einmaligDiscount,
      monatlichDiscount,
      jaehrlichDiscount,
      einmaligTotal: Math.max(0, einmaligSubtotal - einmaligDiscount),
      monatlichTotal: Math.max(0, monatlichSubtotal - monatlichDiscount),
      jaehrlichTotal: Math.max(0, jaehrlichSubtotal - jaehrlichDiscount),
      laufendTotal: Math.max(0, monatlichSubtotal - monatlichDiscount) + Math.max(0, jaehrlichSubtotal - jaehrlichDiscount)
    }
  }

  const pricingCalculation = calculatePricing()
  const today = new Date()
  const validUntil = new Date(today.getTime() + 8 * 24 * 60 * 60 * 1000)

  const handleExportPdf = async () => {
    if (!savedQuoteId) {
      toast({
        title: "Angebot nicht gespeichert",
        description: "Bitte speichern Sie das Angebot zuerst.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🔍 PDF Export - savedQuoteId:', savedQuoteId);
      console.log('🔍 Using Browser-based PDF generation (100% identical to preview)');
      
      // Save original title
      const originalTitle = document.title;
      
      // Set custom document title for better PDF filename
      const quoteNumber = savedQuoteNumber || generateQuoteNumber();
      const customerName = customerInfo?.company || customerInfo?.name || 'Kunde';
      const cleanCustomerName = customerName.replace(/[^a-zA-Z0-9äöüÄÖÜß\s]/g, '').trim();
      document.title = `Angebot_${quoteNumber}_${cleanCustomerName}`;
      
      // Add print class to body for print-specific styling
      document.body.classList.add('printing');
      
      // Open all details elements (AGB) before printing
      const detailsElements = document.querySelectorAll('details');
      const originalStates = [];
      detailsElements.forEach((details, index) => {
        originalStates[index] = details.open;
        details.open = true;
      });
      
      // Small delay to ensure CSS is applied
      setTimeout(() => {
        // Trigger browser print dialog
        window.print();
        
        // Remove print class and restore title/states after printing
        setTimeout(() => {
          document.body.classList.remove('printing');
          document.title = originalTitle;
          // Restore AGB states
          detailsElements.forEach((details, index) => {
            details.open = originalStates[index];
          });
        }, 100);
      }, 100);

      toast({
        title: "PDF-Druckdialog geöffnet",
        description: "Wählen Sie 'Als PDF speichern' im Druckdialog.",
      });
    } catch (error) {
      console.error('🔍 PDF Generation error:', error);
      document.body.classList.remove('printing');
      toast({
        title: "Fehler",
        description: `PDF-Export fehlgeschlagen: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleEmailWithPdf = async () => {
    if (!savedQuoteId) {
      toast({
        title: "Angebot nicht gespeichert",
        description: "Bitte speichern Sie das Angebot zuerst.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get customer email
      const customerEmail = customerInfo?.email || '';
      const customerName = customerInfo?.company || customerInfo?.name || 'Kunde';
      const quoteNumber = savedQuoteNumber || generateQuoteNumber();
      const companyName = companyInfo?.name || 'Unsere Firma';
      
      if (!customerEmail) {
        toast({
          title: "Keine Kunden-Email",
          description: "Bitte hinterlegen Sie eine Email-Adresse für den Kunden.",
          variant: "destructive",
        });
        return;
      }

      // Calculate total with VAT
      const subtotal = pricing?.subtotal || 0;
      const total = subtotal * 1.19;

      // Create email template
      const subject = encodeURIComponent(`Ihr Angebot Nr. ${quoteNumber} von ${companyName}`);
      
      const body = encodeURIComponent(`Sehr geehrte Damen und Herren,

anbei erhalten Sie unser Angebot Nr. ${quoteNumber}.

Angebotssumme: ${total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € (inkl. 19% MwSt.)
Gültig bis: ${formatDate(validUntil)}

Wir freuen uns auf Ihre Rückmeldung und stehen Ihnen bei Fragen gerne zur Verfügung.

Mit freundlichen Grüßen
${companyName}

---
SCHRITT 1: Speichern Sie das PDF über Strg+P (Windows) oder Cmd+P (Mac)
SCHRITT 2: Fügen Sie das gespeicherte PDF dieser Email als Anhang hinzu
SCHRITT 3: Versenden Sie die Email`);

      // Open mailto link directly
      const mailtoLink = `mailto:${customerEmail}?subject=${subject}&body=${body}`;
      
      // Use window.open for mailto to prevent navigation issues
      // For mailto links, window.open works better than createElement
      window.open(mailtoLink, '_self');
      
      toast({
        title: "Email-Programm wird geöffnet ✉️",
        description: "Drücken Sie Strg+P/Cmd+P um das PDF zu speichern, dann anhängen.",
        duration: 6000,
      });

    } catch (error) {
      console.error('Email with PDF error:', error);
      toast({
        title: "Fehler",
        description: `Email konnte nicht vorbereitet werden: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleSendEmail = async () => {
    if (!savedQuoteId) {
      toast({
        title: "Angebot nicht gespeichert",
        description: "Bitte speichern Sie das Angebot zuerst, bevor Sie es per E-Mail versenden.",
        variant: "destructive",
      });
      return;
    }

    if (!customerInfo.email) {
      toast({
        title: "E-Mail-Adresse fehlt",
        description: "Bitte geben Sie eine E-Mail-Adresse für den Kunden ein.",
        variant: "destructive",
      });
      return;
    }

    try {
      toast({
        title: "E-Mail wird versendet...",
        description: "Das kann einen Moment dauern.",
      });

      await quotesApi.sendEmail(savedQuoteId);

      toast({
        title: "E-Mail erfolgreich versendet ✅",
        description: `Das Angebot wurde an ${customerInfo.email} gesendet.`,
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Fehler beim E-Mail-Versand",
        description: "Die E-Mail konnte nicht versendet werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    }
  };

  const [copyingLink, setCopyingLink] = React.useState(false);

  const handleCopyViewLink = async () => {
    if (!savedQuoteId) {
      toast({
        title: "Bitte Angebot zuerst speichern",
        variant: "destructive",
      });
      return;
    }

    setCopyingLink(true);
    try {
      // Generate or get view URL
      const response = await quotesApi.generateViewUrl(savedQuoteId);
      const viewUrl = response.data.viewUrl;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(viewUrl);
      
      toast({
        title: "Link kopiert! 🔗",
        description: "Der öffentliche Ansichts-Link wurde in die Zwischenablage kopiert.",
      });
    } catch (error) {
      console.error('Error copying view link:', error);
      toast({
        title: "Fehler",
        description: "Link konnte nicht kopiert werden.",
        variant: "destructive",
      });
    } finally {
      setCopyingLink(false);
    }
  };

  const handlePrint = () => {
    // Create a print-friendly version of just the PDF preview content
    const printContent = document.querySelector('.pdf-preview-content');
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Angebot - ${generateQuoteNumber()}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.4; }
              .no-print { display: none; }
              table { border-collapse: collapse; width: 100%; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f5f5f5; font-weight: bold; }
              .header { margin-bottom: 30px; }
              .customer-info { background: #f9f9f9; padding: 15px; margin: 20px 0; }
              .totals { font-weight: bold; background: #e8f5e8; }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    } else {
      // Fallback to regular print
      window.print();
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" style={{
      '--color-einmalig': companyColors.einmalig,
      '--color-monatlich': companyColors.monatlich,
      '--color-jaehrlich': companyColors.jaehrlich
    }}>
      {/* Action Buttons */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 justify-between items-center">
          <div className="flex items-center gap-3">
            <Checkbox 
              id="detailed-descriptions"
              checked={includeDetailedDescriptions}
              onCheckedChange={handleIncludeDetailedDescriptionsChange}
            />
            <Label htmlFor="detailed-descriptions" className="text-sm font-medium">
              Detaillierte Service-Beschreibungen anhängen
            </Label>
          </div>
          
          <div className="flex gap-3">
            <Button onClick={handleExportPdf} className="flex items-center gap-2" disabled={!savedQuoteId}>
              <Printer className="w-4 h-4" />
              Als PDF drucken {!savedQuoteId && '(Angebot zuerst speichern)'}
            </Button>
            
            <Button 
              onClick={handleCopyViewLink} 
              variant="outline"
              className="flex items-center gap-2" 
              disabled={!savedQuoteId || copyingLink}
            >
              {copyingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
              {copyingLink ? 'Generiere Link...' : 'Link kopieren'} {!savedQuoteId && '(Angebot zuerst speichern)'}
            </Button>
            
            {/* Video Recording Button - Disabled with Coming Soon message */}
            <Button 
              variant="outline" 
              className="flex items-center gap-2 bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
              disabled
            >
              <Video className="w-4 h-4" />
              Video-Erklärung (Bald verfügbar)
            </Button>
            
            {/* Video Recording Dialog - Hidden for now */}
            <Dialog open={showVideoRecorder} onOpenChange={setShowVideoRecorder}>
              <DialogTrigger asChild>
                <div style={{ display: 'none' }} />
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Video-Erklärung für Angebot aufnehmen</DialogTitle>
                </DialogHeader>
                <VideoRecorder 
                  quoteId={savedQuoteId}
                  onVideoUploaded={(videoData) => {
                    setQuoteVideos(prev => [...prev, videoData])
                    setShowVideoRecorder(false)
                    toast({
                      title: "Video erfolgreich erstellt!",
                      description: "Das Video wurde hochgeladen und ist bereit zum Teilen."
                    })
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </Card>

      {/* PDF Preview */}
      <Card className="bg-white shadow-lg">
        <div data-quote-preview className="p-8 space-y-8">
          {/* Header */}
          <div className="flex justify-between items-start border-b pb-6">
            <div>
              <div className="w-32 h-16 mb-4 flex items-center justify-center overflow-hidden bg-transparent">
                {companyInfo.logo ? (
                  <img 
                    src={companyInfo.logo} 
                    alt="Firmenlogo" 
                    className="w-full h-full object-contain bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                  />
                ) : (
                  <div 
                    className="w-full h-full flex items-center justify-center rounded-lg text-slate-400 font-bold text-xl border-2 border-dashed border-slate-300 bg-transparent"
                  >
                    LOGO
                  </div>
                )}
              </div>
              <div className="text-sm text-slate-600">
                <p className="font-semibold">{companyInfo.name}</p>
                <p>{companyInfo.address}</p>
                <p>{companyInfo.city}</p>
                <p>Tel: {companyInfo.phone}</p>
                <p>E-Mail: {companyInfo.email}</p>
              </div>
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">ANGEBOT</h1>
              <div className="text-sm text-slate-600 space-y-1">
                <p><span className="font-medium">Angebots-Nr.:</span> {savedQuoteNumber || generateQuoteNumber()}</p>
                <p><span className="font-medium">Datum:</span> {formatDate(today)}</p>
                <p><span className="font-medium">Gültig bis:</span> {formatDate(validUntil)}</p>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div>
            <h3 className="text-lg font-semibold mb-3 text-slate-800">Auftraggeber:</h3>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="font-semibold">{customerInfo.company || 'Firmenname'}</p>
              <p>{customerInfo.name || 'Ansprechpartner'}</p>
              <p>{customerInfo.address || 'Adresse'}</p>
              <p>{customerInfo.city || 'PLZ Ort'}</p>
              {customerInfo.phone && <p>Tel: {customerInfo.phone}</p>}
              {customerInfo.email && <p>E-Mail: {customerInfo.email}</p>}
            </div>
          </div>

          {/* Quote Introduction (KI Generated) */}
          {(quoteIntro || localIntroText) && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Angebots-Einleitung:</h3>
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                <div 
                  className="text-slate-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: (quoteIntro || localIntroText)
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      .replace(/\n/g, '<br />')
                  }}
                />
              </div>
            </div>
          )}

          {/* Additional Information */}
          {additionalInfo && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Weitere Informationen:</h3>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                  {additionalInfo}
                </p>
              </div>
            </div>
          )}

          {/* Warum dieses Angebot */}
          {customerArguments && (
            <div>
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Warum dieses Angebot für Sie:</h3>
              <div className="p-4 rounded-lg border-l-4" style={{ borderLeftColor: companyColors.primary, backgroundColor: `${companyColors.primary}08` }}>
                <div className="text-slate-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: customerArguments }} />
              </div>
            </div>
          )}

          {/* Quote Items - One-time costs */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-slate-800">Einmalige Leistungen:</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-slate-200">
                    <th className="text-left py-2 px-2 text-sm font-semibold text-slate-600">Pos.</th>
                    <th className="text-left py-2 px-2 text-sm font-semibold text-slate-600">Leistung</th>
                    <th className="text-right py-2 px-2 text-sm font-semibold text-slate-600">Menge</th>
                    <th className="text-right py-2 px-2 text-sm font-semibold text-slate-600">Einzelpreis</th>
                    <th className="text-right py-2 px-2 text-sm font-semibold text-slate-600">Gesamtpreis</th>
                  </tr>
                </thead>
                <tbody>
                  {oneTimeItems.map((item, index) => (
                    <tr key={item.position} className="border-b border-slate-100">
                      <td className="py-3 px-2 text-sm">{index + 1}</td>
                      <td className="py-3 px-2">
                        <div className={item.isAlternativeOption ? 'pl-6' : ''}>
                          <div className="flex items-center gap-2">
                            <p className={`font-medium text-sm ${item.isAlternativeOption ? 'text-purple-700' : ''}`}>
                              {item.isAlternativeOption && '└→ '}
                              {item.description}
                            </p>
                            {item.isAlternativeActive && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                                ✓ Gewählt
                              </span>
                            )}
                            {item.isAlternativeOption && (
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full font-medium">
                                Alternative
                              </span>
                            )}
                            {item.selectedAlternative && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                                {item.selectedAlternative}
                              </span>
                            )}
                            {item.isOptional && !item.isAlternativeOption && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                Optional
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 mt-1">{item.details}</p>
                          {item.selectedAlternativeDescription && (
                            <p className="text-xs text-blue-600 mt-1 italic">
                              ℹ️ {item.selectedAlternativeDescription}
                            </p>
                          )}
                          {item.notice && (
                            <p className="text-xs mt-1 font-medium" style={{ color: companyColors.einmalig }}>
                              ⚠️ {item.notice}
                            </p>
                          )}
                          {item.serviceDiscount > 0 && (
                            <p className="text-xs text-green-600 mt-1 font-medium">
                              💰 Service-Rabatt: -{item.customDiscountInfo.value}{item.customDiscountInfo.type === 'percentage' ? '%' : '€'}
                            </p>
                          )}
                          {item.notes && (
                            <p className="text-xs text-blue-600 mt-1 italic">
                              💬 {item.notes}
                            </p>
                          )}
                          {item.isOptional && !item.isAlternativeOption && (
                            <p className="text-xs text-yellow-600 mt-1 italic">
                              Diese Position ist optional und nicht im Endpreis enthalten.
                            </p>
                          )}
                          {item.isAlternativeOption && (
                            <p className="text-xs text-purple-600 mt-1 italic">
                              💡 Alternative Position zu "{item.parentService}" - wählbar statt der Hauptposition
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 text-right text-sm">{item.quantity}</td>
                      <td className="py-3 px-2 text-right text-sm">
                        {item.unitPrice.toLocaleString('de-DE')}€
                      </td>
                      <td className="py-3 px-2 text-right text-sm font-medium">
                        {item.total.toLocaleString('de-DE')}€
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Kostenübersicht einmalige Leistungen - vereinheitlicht */}
            <div className="p-6 rounded-lg border mt-6" style={{ 
              backgroundColor: `${companyColors.einmalig}15`, 
              borderColor: `${companyColors.einmalig}50` 
            }}>
              <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.einmalig }}>Kostenübersicht einmalige Leistungen</h4>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Nettobetrag:</span>
                  <span className="font-medium">€{pricingCalculation.einmaligSubtotal.toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
                
                {pricingCalculation.einmaligDiscount > 0 && discount?.oneTime && (
                  <div className="flex justify-between text-green-700">
                    <span>Rabatt ({discount.oneTime.type === 'percentage' ? `${discount.oneTime.value}%` : `${discount.oneTime.value}€`}):</span>
                    <span className="font-medium">-€{pricingCalculation.einmaligDiscount.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span>Netto-Endpreis:</span>
                  <span className="font-medium">€{pricingCalculation.einmaligTotal.toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
                
                <div className="flex justify-between border-t pt-2 mt-2" style={{ borderColor: `${companyColors.einmalig}50` }}>
                  <span>MwSt. (19%):</span>
                  <span className="font-medium">€{(pricingCalculation.einmaligTotal * 0.19).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
                
                <div className="flex justify-between border-t-2 pt-2 mt-2" style={{ borderColor: companyColors.einmalig }}>
                  <span className="font-bold text-lg">Gesamtbetrag inkl. MwSt.:</span>
                  <span className="font-bold text-lg" style={{ color: companyColors.einmalig }}>€{(pricingCalculation.einmaligTotal * 1.19).toLocaleString('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Services */}
          {/* Monatliche Leistungen - moved from below */}
          {monthlyItems.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: companyColors.monatlich }}>Monatliche Leistungen:</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: `${companyColors.monatlich}33` }}>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Pos.</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Leistung</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Menge</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.monatlich }}>Monatspreis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyItems.map((item, index) => (
                      <tr key={item.position} className="border-b" style={{ borderColor: `${companyColors.monatlich}1a` }}>
                        <td className="py-3 px-2 text-sm">{index + 1}</td>
                        <td className="py-3 px-2">
                          <div className={item.isAlternativeOption ? 'pl-6' : ''}>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium text-sm ${item.isAlternativeOption ? 'text-purple-700' : ''}`}>
                                {item.isAlternativeOption && '└→ '}
                                {item.description}
                              </p>
                              {item.isAlternativeActive && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                                  ✓ Gewählt
                                </span>
                              )}
                              {item.isAlternativeOption && (
                                <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full font-medium">
                                  Alternative
                                </span>
                              )}
                              {item.selectedAlternative && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                                  {item.selectedAlternative}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-600 mt-1">{item.details}</p>
                            {item.selectedAlternativeDescription && (
                              <p className="text-xs text-blue-600 mt-1 italic">
                                ℹ️ {item.selectedAlternativeDescription}
                              </p>
                            )}
                            {item.notice && (
                              <p className="text-xs mt-1 font-medium" style={{ color: companyColors.monatlich }}>
                                ⚠️ {item.notice}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right text-sm">{item.quantity}</td>
                        <td className="py-3 px-2 text-right text-sm font-medium" style={{ color: companyColors.monatlich }}>
                          {item.total.toLocaleString('de-DE')}€/Monat
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Kostenübersicht monatliche Leistungen direkt unter Monatliche Leistungen */}
              <div className="mt-4 p-6 rounded-lg border" style={{ 
                backgroundColor: `${companyColors.monatlich}15`, 
                borderColor: `${companyColors.monatlich}50` 
              }}>
                <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.monatlich }}>Kostenübersicht monatliche Leistungen</h4>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Nettobetrag:</span>
                    <span className="font-medium">€{pricingCalculation.monatlichSubtotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  
                  {pricingCalculation.monatlichDiscount > 0 && discount?.monthly && (
                    <div className="flex justify-between text-green-700">
                      <span>Rabatt ({discount.monthly.type === 'percentage' ? `${discount.monthly.value}%` : `${discount.monthly.value}€`}):</span>
                      <span className="font-medium">-€{pricingCalculation.monatlichDiscount.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}/Monat</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span>Netto-Endpreis:</span>
                    <span className="font-medium">€{pricingCalculation.monatlichTotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  
                  <div className="flex justify-between border-t pt-2 mt-2" style={{ borderColor: `${companyColors.monatlich}50` }}>
                    <span>MwSt. (19%):</span>
                    <span className="font-medium">€{(pricingCalculation.monatlichTotal * 0.19).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  
                  <div className="flex justify-between border-t-2 pt-2 mt-2" style={{ borderColor: companyColors.monatlich }}>
                    <span className="font-bold text-lg">Gesamtbetrag inkl. MwSt.:</span>
                    <span className="font-bold text-lg" style={{ color: companyColors.monatlich }}>€{(pricingCalculation.monatlichTotal * 1.19).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Monat</span>
                  </div>
                  
                  <div className="text-xs mt-3 italic" style={{ color: companyColors.monatlich }}>
                    * Monatliche Leistungen werden ab Projektstart monatlich abgerechnet und sind separat zu den weiteren Kosten zu verstehen.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Yearly Services */}
          {yearlyItems.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4" style={{ color: companyColors.jaehrlich }}>Jährliche Leistungen:</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2" style={{ borderColor: `${companyColors.jaehrlich}33` }}>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Pos.</th>
                      <th className="text-left py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Leistung</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Menge</th>
                      <th className="text-right py-2 px-2 text-sm font-semibold" style={{ color: companyColors.jaehrlich }}>Jahrespreis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {yearlyItems.map((item, index) => (
                      <tr key={item.position} className="border-b" style={{ borderColor: `${companyColors.jaehrlich}1a` }}>
                        <td className="py-3 px-2 text-sm">{index + 1}</td>
                        <td className="py-3 px-2">
                          <div className={item.isAlternativeOption ? 'pl-6' : ''}>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium text-sm ${item.isAlternativeOption ? 'text-purple-700' : ''}`}>
                                {item.isAlternativeOption && '└→ '}
                                {item.description}
                              </p>
                              {item.isAlternativeActive && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                                  ✓ Gewählt
                                </span>
                              )}
                              {item.isAlternativeOption && (
                                <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full font-medium">
                                  Alternative
                                </span>
                              )}
                              {item.selectedAlternative && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                                  {item.selectedAlternative}
                                </span>
                              )}
                              {item.isOptional && !item.isAlternativeOption && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                                  Optional
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-600 mt-1">{item.details}</p>
                            {item.selectedAlternativeDescription && (
                              <p className="text-xs text-blue-600 mt-1 italic">
                                ℹ️ {item.selectedAlternativeDescription}
                              </p>
                            )}
                            {item.notice && (
                              <p className="text-xs mt-1 font-medium" style={{ color: companyColors.jaehrlich }}>
                                ⚠️ {item.notice}
                              </p>
                            )}
                            {item.isOptional && (
                              <p className="text-xs text-yellow-600 mt-1 italic">
                                Diese Position ist optional und nicht im Endpreis enthalten.
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right text-sm">{item.quantity}</td>
                        <td className="py-3 px-2 text-right text-sm font-medium" style={{ color: companyColors.jaehrlich }}>
                          {item.total.toLocaleString('de-DE')}€/Jahr
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Kostenübersicht jährliche Leistungen - vereinheitlicht */}
              <div className="mt-4 p-6 rounded-lg border" style={{ 
                backgroundColor: `${companyColors.jaehrlich}15`, 
                borderColor: `${companyColors.jaehrlich}50` 
              }}>
                <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.jaehrlich }}>Kostenübersicht jährliche Leistungen</h4>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Nettobetrag:</span>
                    <span className="font-medium">€{pricingCalculation.jaehrlichSubtotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  
                  {pricingCalculation.jaehrlichDiscount > 0 && discount?.yearly && (
                    <div className="flex justify-between text-green-700">
                      <span>Rabatt ({discount.yearly.type === 'percentage' ? `${discount.yearly.value}%` : `${discount.yearly.value}€`}):</span>
                      <span className="font-medium">-€{pricingCalculation.jaehrlichDiscount.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}/Jahr</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span>Netto-Endpreis:</span>
                    <span className="font-medium">€{pricingCalculation.jaehrlichTotal.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  
                  <div className="flex justify-between border-t pt-2 mt-2" style={{ borderColor: `${companyColors.jaehrlich}50` }}>
                    <span>MwSt. (19%):</span>
                    <span className="font-medium">€{(pricingCalculation.jaehrlichTotal * 0.19).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  
                  <div className="flex justify-between border-t-2 pt-2 mt-2" style={{ borderColor: companyColors.jaehrlich }}>
                    <span className="font-bold text-lg">Gesamtbetrag inkl. MwSt.:</span>
                    <span className="font-bold text-lg" style={{ color: companyColors.jaehrlich }}>€{(pricingCalculation.jaehrlichTotal * 1.19).toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}/Jahr</span>
                  </div>
                  
                  <div className="text-xs mt-3 italic" style={{ color: companyColors.jaehrlich }}>
                    * Jährliche Leistungen werden einmal pro Jahr abgerechnet und sind separat zu den weiteren Kosten zu verstehen.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Descriptions (if enabled) */}
          {includeDetailedDescriptions && (
            <div className="border-t pt-8">
              <h3 className="text-lg font-semibold mb-6 text-slate-800">Detaillierte Leistungsbeschreibungen:</h3>
              
              {/* Einmalige Leistungen */}
              {oneTimeItems.filter(item => item.detailedDescription).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.einmalig }}>Einmalige Kosten:</h4>
                  <div className="space-y-6">
                    {oneTimeItems.filter(item => item.detailedDescription).map((item, index) => (
                      <div key={item.position} className="p-4 rounded-lg border" style={{ 
                        backgroundColor: `${companyColors.einmalig}15`, 
                        borderColor: `${companyColors.einmalig}50` 
                      }}>
                        <h5 className="font-semibold text-slate-800 mb-2">{index + 1}. {item.description}</h5>
                        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                          {item.detailedDescription}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Monatliche Leistungen */}
              {monthlyItems.filter(item => item.detailedDescription).length > 0 && (
                <div className="mb-8">
                  <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.monatlich }}>Monatliche Kosten:</h4>
                  <div className="space-y-6">
                    {monthlyItems.filter(item => item.detailedDescription).map((item, index) => (
                      <div key={item.position} className="p-4 rounded-lg border" style={{ 
                        backgroundColor: `${companyColors.monatlich}15`, 
                        borderColor: `${companyColors.monatlich}50` 
                      }}>
                        <h5 className="font-semibold text-slate-800 mb-2">{index + 1}. {item.description}</h5>
                        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                          {item.detailedDescription}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Jährliche Leistungen */}
              {yearlyItems.filter(item => item.detailedDescription).length > 0 && (
                <div>
                  <h4 className="text-md font-semibold mb-4" style={{ color: companyColors.jaehrlich }}>Jährliche Kosten:</h4>
                  <div className="space-y-6">
                    {yearlyItems.filter(item => item.detailedDescription).map((item, index) => (
                      <div key={item.position} className="p-4 rounded-lg border" style={{ 
                        backgroundColor: `${companyColors.jaehrlich}15`, 
                        borderColor: `${companyColors.jaehrlich}50` 
                      }}>
                        <h5 className="font-semibold text-slate-800 mb-2">{index + 1}. {item.description}</h5>
                        <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                          {item.detailedDescription}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Custom Quote Sections - Debug and show always if available */}
          {quoteSections && quoteSections.length > 0 && (
            <div className="border-t pt-8">
              <h3 className="text-lg font-semibold mb-6 text-slate-800">Weitere Informationen:</h3>
              <div className="space-y-6">
                {quoteSections.map((section, index) => (
                  <div key={section.id || index}>
                    <h4 className="font-semibold text-slate-800 mb-3">{section.title}:</h4>
                    <div 
                      className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-lg border"
                      dangerouslySetInnerHTML={{
                        __html: section.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                          .replace(/• /g, '<span style="color: #2563eb;">•</span> ')
                          .replace(/✓ /g, '<span style="color: #16a34a; font-weight: bold;">✓</span> ')
                          .replace(/(\d+)\. /g, '<strong style="color: #7c3aed;">$1.</strong> ')
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline;">$1</a>')
                          .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 200px; height: auto; margin: 10px 0;" />')
                          .replace(/\n/g, '<br />')
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Debug: Show quote sections info */}
          {/* Debug section removed */}

          {/* Was kommt als Nächstes */}
          {nextSteps && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-3 text-slate-800">Was kommt als Nächstes:</h3>
              <div className="p-4 rounded-lg bg-blue-50 border-l-4 border-blue-400">
                <div className="text-slate-700 leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: nextSteps }} />
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="space-y-4 text-sm text-slate-600">
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Zahlungsbedingungen:</h4>
              <p>{(() => {
                switch(paymentTerms) {
                  case '30-tage-netto': return '30 Tage netto'
                  case '14-tage-netto': return '14 Tage netto'
                  case 'sofort-faellig': return 'Sofort fällig'
                  case 'bei-lieferung': return 'Bei Lieferung'
                  case '50-30-20': return '50% Anzahlung, 30% bei Zwischenabnahme, 20% bei Fertigstellung'
                  case '50-50': return '50% Anzahlung, 50% bei Fertigstellung'
                  case 'custom': return customPaymentTerms || '30 Tage netto'
                  default:
                    // Handle saved-X pattern
                    if (paymentTerms?.startsWith('saved-')) {
                      const index = parseInt(paymentTerms.replace('saved-', ''))
                      return savedCustomTerms?.[index] || customPaymentTerms || '30 Tage netto'
                    }
                    return paymentTerms || '30 Tage netto'
                }
              })()}</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Lieferzeit:</h4>
              <p>{deliveryTime || '4-6 Wochen nach Auftragserteilung'}</p>
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Gültigkeit:</h4>
              <p>{validity || '8 Tage gültig ab Erstellungsdatum'}</p>
            </div>

            {warranty && (
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Gewährleistung:</h4>
                <p>{warranty}</p>
              </div>
            )}

            {/* AGBs aus Firmeneinstellungen - als Dropdown */}
            {companyInfo.termsAndConditions && (
              <div className="pt-4 border-t">
                <details className="group">
                  <summary className="font-semibold text-slate-800 cursor-pointer flex items-center justify-between hover:text-blue-600 transition-colors">
                    <span>Allgemeine Geschäftsbedingungen</span>
                    <svg className="w-5 h-5 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="text-xs text-slate-700 whitespace-pre-wrap mt-3 pt-3 border-t border-slate-200">
                    {companyInfo.termsAndConditions.split('\n').map((line, idx) => {
                      // Format bold text with **text**
                      const formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                      return (
                        <p key={idx} dangerouslySetInnerHTML={{ __html: formattedLine }} className="mb-1" />
                      );
                    })}
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-slate-500 border-t pt-4">
            <p>{companyInfo.name} • {companyInfo.address} • {companyInfo.city}</p>
            <p>Tel: {companyInfo.phone} • E-Mail: {companyInfo.email} • Web: {companyInfo.website}</p>
          </div>
        </div>
      </Card>

      {/* Video Gallery for Quote */}
      {quoteVideos.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center text-slate-800">
              <Video className="h-5 w-5 mr-2 text-red-600" />
              Video-Erklärungen zu diesem Angebot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quoteVideos.map((video, index) => (
                <div key={video.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="aspect-video bg-slate-100 rounded-lg mb-3 flex items-center justify-center">
                    <video 
                      src={video.url} 
                      controls 
                      className="w-full h-full rounded-lg"
                      poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjFmNWY5Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY0NzQ4YiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkFuZ2Vib3RzLVZpZGVvPC90ZXh0Pjwvc3ZnPg=="
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium text-slate-800 text-sm">{video.title}</h4>
                    <div className="text-xs text-slate-500">
                      {new Date(video.createdAt).toLocaleDateString('de-DE')} • Video #{index + 1}
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(video.shareLink)
                          toast({
                            title: "Link kopiert!",
                            description: "Der Video-Link wurde in die Zwischenablage kopiert."
                          })
                        }}
                        className="flex-1"
                      >
                        <Link className="h-3 w-3 mr-1" />
                        Link kopieren
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm('Video wirklich löschen?')) {
                            setQuoteVideos(prev => prev.filter(v => v.id !== video.id))
                            toast({
                              title: "Video gelöscht",
                              description: "Das Video wurde entfernt."
                            })
                          }
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="mr-1">💡</span>
                <strong>Tipp:</strong> Diese Video-Erklärungen können Sie über den Link mit Ihren Kunden teilen, 
                um Ihr Angebot persönlich zu erläutern.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PdfPreview;